import {
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { getUniqueId } from '../../../functions/get-unique-id';
import { Action } from '../../../models/core/action';
import { Expanded } from '../../../models/core/expanded';
import { CasePipe, CaseType } from '../../../pipes/case.pipe';
import {
  clearCardScrollMargin,
  scrollCardIntoViewIfNeeded,
} from '../../../utilities/card-scroll.helper';
import { ExpandButtonComponent } from '../buttons/expand-button.component';
import { IconButtonComponent } from '../buttons/icon-button.component';
import { IconComponent } from '../labels/icon.component';
import { EmptyStateComponent } from './empty-state.component';
import { MenuComponent } from './menu.component';

@Component({
  selector: 'app-card',
  imports: [
    CommonModule,
    CdkDragHandle,
    CdkDrag,
    ExpandButtonComponent,
    CasePipe,
    IconComponent,
    CdkDragPlaceholder,
    EmptyStateComponent,
    IconButtonComponent,
    MenuComponent,
  ],
  template: `
    <div
      #cardRoot
      class="tw-w-full tw-min-w-0 tw-border tw-rounded-md tw-border-[var(--sed-grey-40-color)] tw-bg-white"
      [ngClass]="{
        'tw-outline tw-outline-2 tw-outline-[var(--sed-engaged-black-color)] tw-outline-offset-2':
          moveEditModeActive,
      }"
      [style.min-height]="minHeight"
      cdkDrag
      [cdkDragDisabled]="!showDragDrop"
      [class]="classes"
    >
      <div
        class="tw-bg-[var(--sed-grey-60-color)] tw-border-[var(--sed-black-80-color)]
        tw-border-2 tw-border-dotted tw-rounded-md tw-animate-pulse tw-h-[120px] tw-mb-4"
        *cdkDragPlaceholder
      ></div>
      <!-- header -->
      <div
        class="tw-w-full tw-flex tw-flex-wrap tw-items-center tw-pt-1 tw-pb-1 tw-px-4
        tw-border-b tw-rounded-t-md tw-border-[var(--sed-grey-40-color)]
        "
        [class]="headerClasses"
        [ngClass]="{
          'tw-rounded-b-md': !expanded,
          'sm:tw-rounded-b-none': !showExpand,
          'tw-cursor-move': showDragDrop,
          'tw-cursor-pointer': rowClick && !showDragDrop,
          'max-sm:tw-cursor-pointer': !rowClick && !showExpand && !showDragDrop,
        }"
        [attr.aria-label]="
          (ariaLabel || label) + (moveEditModeActive ? '; move mode on' : '')
        "
        [style.color]="textColor"
        [style.background-color]="bgColor"
        cdkDragHandle
        (click)="rowClicked()"
        (keyup.enter)="rowClicked()"
        (keyup.space)="rowClicked()"
        [tabindex]="rowClick ? 0 : -1"
      >
        <div
          class="tw-grow tw-flex tw-items-center tw-w-2/3
        "
        >
          @if (icon) {
            <app-icon
              [name]="icon"
              [size]="type === 'Large' ? '24px' : '18px'"
              [color]="textColor"
              class="tw-pr-2"
            />
          }

          <div class="tw-grow">
            @if (headingLevel === 1) {
              <h1
                [class]="headingClass"
                [style.color]="textColor"
                [attr.aria-label]="ariaLabel || label"
              >
                {{ label | case: labelCase }}
              </h1>
            } @else if (headingLevel === 2) {
              <h2
                [class]="headingClass"
                [style.color]="textColor"
                [attr.aria-label]="ariaLabel || label"
              >
                {{ label | case: labelCase }}
              </h2>
            } @else if (headingLevel === 3) {
              <h3
                [class]="headingClass"
                [style.color]="textColor"
                [attr.aria-label]="ariaLabel || label"
              >
                {{ label | case: labelCase }}
              </h3>
            } @else if (headingLevel === 4) {
              <h4
                [class]="headingClass"
                [style.color]="textColor"
                [attr.aria-label]="ariaLabel || label"
              >
                {{ label | case: labelCase }}
              </h4>
            }
          </div>
          @if (showDragDrop && moveContext) {
            <div
              class="tw-relative"
              data-move-trigger
              (mousedown)="$event.stopPropagation()"
              (click)="$event.stopPropagation()"
              (keyup.enter)="$event.stopPropagation()"
              (keyup.space)="$event.stopPropagation()"
              [tabindex]="-1"
            >
              <app-icon-button
                icon="fa-arrows-up-down-left-right fa-light"
                [ariaLabel]="'Move ' + (ariaLabel || label) + ' card'"
                [tooltip]="moveButtonTooltip"
                [ariaExpanded]="moveMenu?.isExpanded"
                ariaHasPopup="menu"
                (click)="openMoveMenu($event)"
              />
              <app-menu
                [items]="moveMenuItemsArray"
                #moveMenu
                appendTo="body"
                [keepOpen]="true"
                (click)="onMoveMenuClick($event)"
              >
                <ng-template #itemTemplate let-item>
                  <div
                    class="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-cursor-pointer"
                  >
                    <div>
                      <span [class]="item.icon"></span>
                      <span class="tw-no-underline" [class]="item.linkClass">
                        {{ item.displayLabel || item.label }}
                      </span>
                    </div>
                    @if (item.badge !== null && item.badge !== '') {
                      <span
                        class="app-options-count tw-rounded-full tw-ml-4 tw-px-2"
                        aria-hidden="true"
                        >{{ item.badge }}</span
                      >
                    }
                  </div>
                </ng-template>
              </app-menu>
            </div>
          } @else if (showDragIcon) {
            <div class="">
              <app-icon
                name="fa-solid fa-light fa-arrows-up-down-left-right"
                [size]="type === 'Large' ? '24px' : '18px'"
                [color]="textColor"
              />
            </div>
          }
          <ng-container [ngTemplateOutlet]="headerTemplate"></ng-container>
        </div>
        <div [class.sm:tw-hidden]="!showExpand">
          <app-expand-button
            #expandButton
            [expanded]="expanded"
            [parentId]="cardId"
            (expand)="onExpandEmit($event)"
            [color]="textColor"
            [disableClick]="effectiveRowClick"
            [label]="label"
          />
        </div>
      </div>

      <!-- body -->

      <div
        class="tw-w-full tw-min-w-0 tw-py-4"
        [class.tw-px-4]="padding"
        [class.tw-hidden]="!expanded"
        [class.sm:tw-block]="!showExpand"
      >
        @if (showEmptyState) {
          <app-empty-state
            height="150px"
            [icon]="emptyIcon"
            [label]="emptyLabel"
            [message]="emptyMessage"
          />
        } @else {
          <ng-content></ng-content>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .large-card {
        font-size: 16px !important;
        font-weight: bold;
        padding-top: 12px;
        padding-bottom: 12px;
      }
      .medium-card {
        font-size: 16px !important;
        font-weight: bold;
      }
      .small-card {
        font-size: 14px !important;
        font-weight: bold;
      }
    `,
  ],
})
export class CardComponent implements Expanded, OnDestroy, OnInit {
  private injector = inject(Injector);

  @Input()
  label: string;

  @Input()
  ariaLabel: string;

  @Input()
  minHeight: string;

  @Input()
  showDragDrop = false;

  @Input()
  showExpand = false;

  @Input()
  rowClick = false;

  @ViewChild('expandButton')
  expandButton: ExpandButtonComponent;

  @Input()
  expanded = true;

  @Input()
  padding = true;

  @Input()
  classes: string;

  @Input()
  headerClasses: string;

  @Input()
  labelCase: CaseType;

  @ContentChild('header')
  headerTemplate: TemplateRef<any>;

  @Input()
  icon: string;

  @Input()
  theme: CardTheme = 'Dark';

  @Input()
  type: CardType = 'Small';

  @Input()
  emptyIcon = 'fa-light fa-folder-open';

  @Input()
  emptyLabel = 'No data available';

  @Input()
  emptyMessage: string;

  @Input()
  showDragIcon = false;

  @Input()
  showEmptyState = false;

  @Input()
  headingLevel: 1 | 2 | 3 | 4 = 2;

  private _moveContext?: {
    canMoveUp: boolean;
    canMoveDown: boolean;
    canMoveLeft: boolean;
    canMoveRight: boolean;
  };

  @Input()
  get moveContext() {
    return this._moveContext;
  }
  set moveContext(value: typeof this._moveContext) {
    this._moveContext = value;
    this.updateMoveMenuItemsArray();
  }

  /** Stable array for menu items so MenuComponent ngOnChanges does not clear menuItems on every CD. */
  moveMenuItemsArray: Action[] = [];

  moveButtonTooltip =
    'Choose a direction from the menu, or hold Shift and press Arrow keys to move.';

  @Output()
  moveRequest = new EventEmitter<{
    direction: 'up' | 'down' | 'left' | 'right';
  }>();

  /** Emitted when move edit mode ends (Escape, click outside, or inactivity). Parent should save layout once. */
  @Output()
  moveEditModeEnd = new EventEmitter<void>();

  /** Emitted when the move menu is opened. Parent may announce for screen readers. */
  @Output()
  moveMenuOpened = new EventEmitter<string>();

  @ViewChild('moveMenu')
  moveMenu: MenuComponent;

  @ViewChild('cardRoot')
  cardRoot: ElementRef<HTMLElement>;

  private moveEditMode = false;

  get moveEditModeActive(): boolean {
    return this.moveEditMode;
  }

  get headingClass(): string {
    if (this.type === 'Large') return 'large-card';
    if (this.type === 'Medium')
      return 'medium-card tw-text-center tw-tracking-wide tw-py-0.5';
    return 'small-card tw-text-center tw-tracking-wide tw-py-0.5';
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.moveContext) return;
    if (!(this.moveMenu?.isExpanded || this.moveEditMode)) return;
    const el = document.activeElement as HTMLElement;
    const inEditable =
      el?.tagName === 'INPUT' ||
      el?.tagName === 'TEXTAREA' ||
      el?.getAttribute?.('contenteditable') === 'true';
    if (inEditable) return;
    const withModifier = event.ctrlKey || event.shiftKey || event.metaKey;
    if (event.key === 'Escape') {
      this.exitMoveEditMode();
      return;
    }

    if (!withModifier) return;
    switch (event.key) {
      case 'ArrowUp':
        if (this.moveContext.canMoveUp) {
          event.preventDefault();
          event.stopPropagation();
          this.moveRequest.emit({ direction: 'up' });
          this.focusCardAfterMove(true, 'up');
        }
        break;
      case 'ArrowDown':
        if (this.moveContext.canMoveDown) {
          event.preventDefault();
          event.stopPropagation();
          this.moveRequest.emit({ direction: 'down' });
          this.focusCardAfterMove(true, 'down');
        }
        break;
      case 'ArrowLeft':
        if (this.moveContext.canMoveLeft) {
          event.preventDefault();
          event.stopPropagation();
          this.moveRequest.emit({ direction: 'left' });
          this.focusCardAfterMove(true, 'left');
        }
        break;
      case 'ArrowRight':
        if (this.moveContext.canMoveRight) {
          event.preventDefault();
          event.stopPropagation();
          this.moveRequest.emit({ direction: 'right' });
          this.focusCardAfterMove(true, 'right');
        }
        break;
    }
  }

  private reopenMenuScrollSub: Subscription | null = null;
  private reopenMenuHideSub: Subscription | null = null;
  private reopenMenuFallbackTimeout: ReturnType<typeof setTimeout> | null =
    null;

  exitMoveEditMode(): void {
    if (!this.moveEditMode) return;
    this.moveEditMode = false;
    const el = this.cardRoot?.nativeElement;
    if (el) clearCardScrollMargin(el);
    this.moveMenu?.hideMenu();
    this.clearReopenMenuScheduling();
    this.moveEditModeEnd.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.moveEditMode) return;
    const target = event.target as Node;
    const root = this.cardRoot?.nativeElement;
    if (!root) return;
    if (root.contains(target)) return;
    if ((target as HTMLElement).closest?.('.p-menu-overlay')) return;
    this.exitMoveEditMode();
  }

  private focusCardAfterMove(
    reopenMenu = false,
    _direction?: 'up' | 'down' | 'left' | 'right',
  ): void {
    afterNextRender(
      () => {
        const el = this.cardRoot?.nativeElement;
        if (el) scrollCardIntoViewIfNeeded(el, _direction);

        if (!reopenMenu) return;
        this.clearReopenMenuScheduling();
        if (!this.moveMenu?.isExpanded) {
          this.scheduleReopenAfterScrollEnd();
          return;
        }
        this.reopenMenuHideSub = this.moveMenu.hide
          .pipe(take(1))
          .subscribe(() => {
            this.reopenMenuHideSub = null;
            this.scheduleReopenAfterScrollEnd();
          });
      },
      { injector: this.injector },
    );
  }

  private clearReopenMenuScheduling(): void {
    this.reopenMenuHideSub?.unsubscribe();
    this.reopenMenuHideSub = null;
    this.reopenMenuScrollSub?.unsubscribe();
    this.reopenMenuScrollSub = null;
    if (this.reopenMenuFallbackTimeout != null) {
      clearTimeout(this.reopenMenuFallbackTimeout);
      this.reopenMenuFallbackTimeout = null;
    }
  }

  private scheduleReopenAfterScrollEnd(): void {
    let done = false;
    const doReopen = (): void => {
      if (done) return;
      done = true;
      this.clearReopenMenuScheduling();
      if (this.moveEditMode) this.reopenMoveMenu();
    };
    this.reopenMenuFallbackTimeout = setTimeout(doReopen, 500);
    this.reopenMenuScrollSub = fromEvent(window, 'scroll', { capture: true })
      .pipe(debounceTime(200), take(1))
      .subscribe(doReopen);
  }

  private reopenMoveMenu(): void {
    if (!this.moveEditMode) return;
    const root = this.cardRoot?.nativeElement;
    if (!root || !this.moveMenu?.menu) return;
    const trigger = root.querySelector<HTMLElement>(
      '[data-move-trigger] button',
    );
    if (!trigger?.isConnected) return;
    try {
      this.moveMenu.menu.show({ currentTarget: trigger });
    } catch {
      // Positioning can throw after move (e.g. when moving up); avoid unhandled error.
    }
  }

  protected cardId: string = getUniqueId('app-card');

  protected bgColor: string;

  protected textColor: string;

  private updateMoveMenuItemsArray(): void {
    const ctx = this._moveContext;
    this.moveMenuItemsArray.length = 0;
    if (!ctx) return;
    this.moveMenuItemsArray.push(
      { label: 'Move Up', key: 'up', disabled: !ctx.canMoveUp },
      { label: 'Move Down', key: 'down', disabled: !ctx.canMoveDown },
      { label: 'Move Left', key: 'left', disabled: !ctx.canMoveLeft },
      { label: 'Move Right', key: 'right', disabled: !ctx.canMoveRight },
    );
  }

  openMoveMenu(event: Event): void {
    event.stopPropagation();
    this.moveEditMode = true;
    // No inactivity auto-close: it fought the keepOpen flow and cut off the
    // screen-reader announcement. Menu closes on Escape, outside click, route
    // change, or opening another card's move menu.
    this.moveMenu?.toggle(event);
    this.moveMenuOpened.emit(this.label || this.ariaLabel || 'Card');
  }

  onMoveMenuClick(action: Action) {
    if (action?.key) {
      this.moveRequest.emit({
        direction: action.key as 'up' | 'down' | 'left' | 'right',
      });
      this.focusCardAfterMove(
        false,
        action.key as 'up' | 'down' | 'left' | 'right',
      );
    }
  }

  ngOnDestroy(): void {
    const el = this.cardRoot?.nativeElement;
    if (el) clearCardScrollMargin(el);
    this.clearReopenMenuScheduling();
  }

  ngOnInit(): void {
    this.bgColor =
      this.theme === 'Dark'
        ? 'var(--sed-black-80-color)'
        : 'var(--sed-open-grey-color)';
    this.textColor =
      this.theme === 'Dark'
        ? 'var(--sed-warm-white-color)'
        : 'var(--sed-engaged-black-color)';

    if (!this.labelCase) {
      this.labelCase = 'sentence';
    }
  }

  private expandButtonHandled = false;

  onExpandEmit(expanded: boolean): void {
    this.expandButtonHandled = true;
    this.expanded = expanded;
  }

  rowClicked(): void {
    if (this.expandButtonHandled) {
      this.expandButtonHandled = false;
      return;
    }
    if (!this.rowClick && this.showExpand && window.innerWidth >= 640) {
      return;
    }
    this.expanded = !this.expanded;
    if (this.expandButton) {
      this.expandButton.expanded = this.expanded;
    }
  }

  get effectiveRowClick(): boolean {
    return this.rowClick || !this.showExpand;
  }
}

export type CardHeaderSize = 'h2' | 'h3';

export type CardType = 'Large' | 'Medium' | 'Small';

export type CardTheme = 'Dark' | 'Light';
